import { startOfNextHour } from '@unimpaired/utils'
import { startOfDay, startOfHour } from 'date-fns'
import { pipe } from 'fp-ts/lib/function'
import * as R from 'ramda'
import { ScheduleEvent } from './DaySchedule.types'
import { differenceInWallClockHours } from './schedule.time.helpers'

/**
 * A Cluster is an hour range `{startHour, endHour}` around a group of
 * events that happen close together in time. The schedule uses clusters to
 * collapse long stretches of empty hours — instead of rendering six blank
 * hours between a morning and an evening event, we render two separate
 * mini-schedules that wrap each cluster of events.
 */
export type Cluster = { startHour: number; endHour: number }

/**
 * Given events grouped by day, return the clusters as an array of start/end
 * hour bounds. Returns null when there are no events at all.
 */
export const getEventClustersAcrossDays = (
  eventsByDay: { startTime: Date; endTime: Date }[][],
  days: Date[],
  clusterDistance: number,
  defaultPageStartMidnightOffset: number | undefined,
  defaultPageEndMidnightOffset: number | undefined,
): Cluster[] | null => {
  if (!eventsByDay.length || eventsByDay.every((events) => !events.length))
    return null
  // Hours since start of the *day*, may be negative or greater than 24
  const getHourOffsetFromDayStart = (time: Date, day: Date) =>
    differenceInWallClockHours(time, startOfDay(day))

  // Flatten into one array of normalized events with hour offsets
  const allEvents = days.flatMap((day, dayIndex) =>
    eventsByDay[dayIndex].map((e) => ({
      startHour: getHourOffsetFromDayStart(e.startTime, day),
      endHour: getHourOffsetFromDayStart(startOfNextHour(e.endTime), day),
    })),
  )

  const sorted = allEvents.sort((a, b) => a.startHour - b.startHour)

  const pageStartMidnightOffset =
    defaultPageStartMidnightOffset ??
    Math.min(...allEvents.map((e) => e.startHour))
  const pageEndMidnightOffset =
    defaultPageEndMidnightOffset ??
    Math.max(...allEvents.map((e) => e.endHour)) - 24

  if (sorted.length === 0)
    return [
      {
        startHour: pageStartMidnightOffset,
        endHour: 24 + pageEndMidnightOffset,
      },
    ]

  const clusters = sorted.reduce<Cluster[]>((clusters, event) => {
    if (clusters.length === 0) {
      return [{ startHour: event.startHour, endHour: event.endHour }]
    }

    const last = clusters[clusters.length - 1]

    // If this event starts within clusterDistance hours of the previous
    // cluster's end, merge them
    if (event.startHour - last.endHour <= clusterDistance) {
      const updated = {
        startHour: last.startHour,
        endHour: Math.max(last.endHour, event.endHour),
      }
      return [...clusters.slice(0, -1), updated]
    }

    return [...clusters, { startHour: event.startHour, endHour: event.endHour }]
  }, [])

  // Extend the first/last clusters to reach the default page start/end
  // offsets from midnight when the offsets are outside the event range
  return pipe(
    clusters,
    R.adjust(0, (firstCluster) => ({
      ...firstCluster,
      startHour: Math.min(firstCluster.startHour, pageStartMidnightOffset),
    })),
    R.adjust(-1, (lastCluster) => ({
      ...lastCluster,
      endHour: Math.max(lastCluster.endHour, 24 + pageEndMidnightOffset),
    })),
  )
}

/**
 * Assign each event in each day to one or more clusters, returning the
 * per-day slice of events a given cluster should render. When clusters is
 * null, each day collapses to a single pseudo-cluster containing everything.
 */
export const clusterEvents = <T extends ScheduleEvent>(
  eventsByDay: T[][],
  days: Date[],
  clusters: Cluster[] | null,
) => {
  if (!clusters)
    return eventsByDay.map((events) => [
      {
        pageStartMidnightOffset: undefined,
        pageEndMidnightOffset: undefined,
        events,
      },
    ])
  return eventsByDay.map((events, day) =>
    clusters?.map((cluster) => ({
      pageStartMidnightOffset: cluster.startHour,
      pageEndMidnightOffset: cluster.endHour - 24,
      events: events.filter(
        (event) =>
          differenceInWallClockHours(
            startOfHour(event.startTime),
            startOfDay(days[day]),
          ) <= cluster.endHour &&
          differenceInWallClockHours(
            startOfNextHour(event.endTime),
            startOfDay(days[day]),
          ) >= cluster.startHour,
      ),
    })),
  )
}
